/**
 * The approve → team "ghost-clone fly" (SKE-4234).
 *
 * When the client approves or requests a change, the client card updates its
 * status in place AND a small ghost of it arcs across to the matching row in
 * the team mirror, which lights up (the row's existing flash) as it lands — so
 * the cause ("I clicked approve on the left") and the effect ("it appeared on
 * my team's side") read as one motion.
 *
 * TRIGGER
 * -------
 * It watches `useDemoMirror().latestNotice` — the same store signal that drives
 * the mirror's flash + toast — and fires once per new status action (keyed by
 * the notice's stable `key`). Comments don't fly. On first load `latestNotice`
 * is null, so nothing flies until the visitor acts.
 *
 * PORTAL / FIXED / ONE-SHOT
 * -------------------------
 * Each flight is a `position: fixed` clone in a body portal — a sibling of the
 * approver `App`, never an ancestor, so it can't re-anchor the fixed confetti
 * canvas (demoReveal.ts). It animates with a single CSS transition (the
 * codebase's reliable idiom) and removes itself on arrival. Under
 * `prefers-reduced-motion: reduce` no clone is spawned at all — the row flash
 * and progress tick already convey the change.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import ImageWithFallback from 'Components/Common/ImageWithFallback'

import type { DemoStatusType } from './demoTypes'
import { useDemoMirror } from './useDemoMirror'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/** Edge length of the flying ghost, in px. */
const FLY_SIZE = 56
const FLY_MS = 680

interface Point {
  x: number
  y: number
}

interface Flight {
  id: string
  from: Point
  to: Point
  thumbnail?: string
  status: DemoStatusType
}

const centreOf = (rect: DOMRect): Point => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
})

const BADGE: Record<
  DemoStatusType,
  { icon: string; className: string } | null
> = {
  approved: { icon: 'fa-solid fa-check', className: 'bg-green-500' },
  requestChange: { icon: 'fa-solid fa-pen', className: 'bg-amber-500' },
  pending: null,
}

const FlightGhost: React.FC<{
  flight: Flight
  onDone: (id: string) => void
}> = ({ flight, onDone }) => {
  // Start centred on the source; a frame later, travel to the target.
  const [point, setPoint] = useState<Point>(flight.from)
  const [landed, setLanded] = useState(false)

  useEffect(() => {
    // Double rAF: the element is painted at `from` on the first frame, and the
    // transition to `to` is committed on the next — a single rAF can coalesce
    // both into one paint (no transition) under concurrent React / some
    // browsers, so the ghost would teleport instead of arc.
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setPoint(flight.to)
        setLanded(true)
      })
    )
    const timer = window.setTimeout(() => onDone(flight.id), FLY_MS + 60)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const badge = BADGE[flight.status]

  return (
    <div
      aria-hidden="true"
      className="absolute left-0 top-0 will-change-transform"
      style={{
        transform: `translate(${point.x - FLY_SIZE / 2}px, ${point.y - FLY_SIZE / 2}px) scale(${landed ? 0.42 : 1.04})`,
        opacity: landed ? 0 : 1,
        transition: `transform ${FLY_MS}ms cubic-bezier(0.34, 1.2, 0.4, 1), opacity ${FLY_MS}ms ease-in`,
      }}
    >
      <div className="relative h-14 w-14 overflow-hidden rounded-xl border border-white bg-neutral-100 shadow-[0_12px_28px_-8px_rgba(16,24,40,0.5)]">
        {flight.thumbnail ? (
          <ImageWithFallback
            variant="media"
            src={flight.thumbnail}
            alt=""
            className="h-full w-full object-cover"
            fallbackClassName="h-full w-full"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-neutral-300">
            <i className="fa-regular fa-image" />
          </span>
        )}
        {badge && (
          <span
            className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white ring-2 ring-white ${badge.className}`}
          >
            <i className={badge.icon} />
          </span>
        )}
      </div>
    </div>
  )
}

const DemoFlyLayer: React.FC = () => {
  const { latestNotice, posts } = useDemoMirror()
  const reduced = usePrefersReducedMotion()
  const lastKeyRef = useRef<string | null>(null)
  const [flights, setFlights] = useState<Flight[]>([])

  useEffect(() => {
    if (!latestNotice || latestNotice.kind !== 'status') {
      return
    }
    if (lastKeyRef.current === latestNotice.key) {
      return
    }
    lastKeyRef.current = latestNotice.key

    if (reduced) {
      return
    }

    // Escape the interpolated id: real staging post ids can contain characters
    // that are not valid in a CSS attribute selector, which would throw a
    // SyntaxError out of querySelector. The static [data-tour] fallback below
    // needs no escaping.
    const escapedPostId = CSS.escape(latestNotice.postId)
    const sourceEl = document.querySelector(`[data-postid="${escapedPostId}"]`)
    const targetEl = document.querySelector(
      `[data-mirror-postid="${escapedPostId}"]`
    )
    if (!targetEl) {
      return
    }

    // Fall back to the client pane's centre if the exact source card is off
    // screen (e.g. scrolled out of the list), so the ghost always has an origin.
    const clientEl = document.querySelector('[data-tour="client"]')
    const sourceRect = (sourceEl ?? clientEl)?.getBoundingClientRect()
    if (!sourceRect) {
      return
    }

    const post = posts.find((p) => p.id === latestNotice.postId)

    setFlights((current) => [
      ...current,
      {
        id: latestNotice.key,
        from: centreOf(sourceRect),
        to: centreOf(targetEl.getBoundingClientRect()),
        thumbnail: post?.thumbnail,
        status: latestNotice.status,
      },
    ])
  }, [latestNotice, posts, reduced])

  const remove = (id: string) =>
    setFlights((current) => current.filter((f) => f.id !== id))

  if (flights.length === 0) {
    return null
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[55]">
      {flights.map((flight) => (
        <FlightGhost key={flight.id} flight={flight} onDone={remove} />
      ))}
    </div>,
    document.body
  )
}

export default DemoFlyLayer
